import React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { addQuery, removeQuery } from 'Helpers/Query';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import { ChevronLeftRounded } from '@material-ui/icons';
import GoalHexagonChevron from 'Assets/Images/goal-polygon-chevron.svg';
import { Typography } from '@material-ui/core';
import { useHistory } from 'react-router';
import { dashboardUri } from 'Consts/clientUris';
import { createNewGoal, goalType, goalId } from 'Consts/Queries';
import useMobileLayout from 'Hooks/useMobileLayout';
import GoalHexagon from 'Assets/Images/goal-polygon.svg';
import { displaySuccess, displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import MyGoalsItem from './MyGoalsItem';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    width: '255px',
    backgroundColor: theme.palette.facet.primaryBlue,
  },
  mobileContainer: {
    display: 'flex',
    width: '100%',
    height: '7vh',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.facet.accentBlue7,
    padding: theme.spacing(0, 2),
  },
  mobileContainerEmpty: {
    display: 'none',
  },
  goalRow: {
    display: 'flex',
    marginBottom: theme.spacing(0.25),
  },
  goalCard: {
    cursor: 'pointer',
  },
  goalText: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
  },
  statusIndicator: {
    width: '5px',
    backgroundColor: theme.palette.facet.lightGray3,
  },
  activeStatus: {
    backgroundColor: '#83C8EB',
  },
  chevronButton: {
    width: '18px',
    height: '15px',
  },
  mobileLeftItems: {
    display: 'flex',
    alignItems: 'center',
  },
  completedHeader: {
    color: '#80C4E7',
    margin: theme.spacing(2, 0, 2, 1),
  },
  emptyContainer: {
    width: '255px',
  },
}));

const MyGoalsList = ({
  goals, activeGoalId, onDelete, updateGoalCollection,
}) => {
  const classes = useStyles();
  const { logResourceEvent } = useEventTrackingLogEvent();

  return goals.map((goal) => {
    const handleClickSidebarGoal = () => {
      removeQuery(createNewGoal);
      removeQuery(goalType);
      addQuery({ goalId: goal.id() });
      logResourceEvent('Client Goals Page: Clicked Goal Card from Sidebar', goal);
    };

    return (
      <div key={goal.id()} className={classes.goalRow}>
        <div className={classNames({
          [classes.statusIndicator]: true,
          [classes.activeStatus]: activeGoalId === goal.id(),
        })}
        />
        <div
          className={classes.goalCard}
          role="presentation"
          onClick={handleClickSidebarGoal}
        >
          <MyGoalsItem
            goal={goal}
            selected={activeGoalId === goal.id()}
            onDelete={onDelete}
            updateGoalCollection={updateGoalCollection}
          />
        </div>
      </div>
    );
  });
};

const MyGoalsSidebar = ({
  activeGoalId, goals, createNew, updateGoalCollection,
}) => {
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const history = useHistory();

  if (isMobile) {
    const activeGoal = !createNew ? goals.find(goal => goal.id() === activeGoalId) : null;
    const goalLabels = activeGoal?.meta().field('type').options().map() || {};
    const goalName = activeGoal?.attr('name') || goalLabels[activeGoal?.attr('type')]?.attr('prompt');
    const bestNextStep = activeGoal?.attr('bestNextStep') === 'true';

    const toggleBestNextStep = async () => {
      try {
        await activeGoal?.attr('bestNextStep', !bestNextStep).submit();
        displaySuccess('Successfully set Best Next Step');
        updateGoalCollection();
      } catch (err) {
        displayErrorMessage(err, 'Could not set Best Next Step');
      }
    };

    return (
      <div className={classNames(classes.mobileContainer)}>
        <span className={classes.mobileLeftItems} onClick={() => history.push(dashboardUri)} role="presentation">
          <IconButton size="small" onClick={() => history.push(dashboardUri)}>
            <ChevronLeftRounded className={classes.chevron} />
          </IconButton>
          <Typography variant="h3" className={classes.goalText}>
            {goalName}
          </Typography>
        </span>
        <IconButton className={classes.chevronButton} size="small" onClick={toggleBestNextStep}>
          <img src={bestNextStep ? GoalHexagonChevron : GoalHexagon} alt="chevron" />
        </IconButton>
      </div>
    );
  }

  const nonCompletedGoals = goals.filter(goal => goal.attr('completed') !== 'true');
  const completedGoals = goals.filter(goal => goal.attr('completed') === 'true');

  const onGoalDelete = () => {
    removeQuery(goalId);
    updateGoalCollection();
  };

  return (
    <div className={classNames({
      [classes.container]: true,
      [classes.emptyContainer]: goals.length === 0 && createNew,
    })}
    >
      {goals.length > 0 && (
        <MyGoalsList activeGoalId={activeGoalId} goals={nonCompletedGoals} onDelete={onGoalDelete} updateGoalCollection={updateGoalCollection} />
      )}

      {completedGoals.length > 0 && (
      <div>
        <div className={classes.completedHeader}>Completed Goals</div>
        <MyGoalsList activeGoalId={activeGoalId} goals={completedGoals} onDelete={onGoalDelete} updateGoalCollection={updateGoalCollection} />
      </div>
      )}
    </div>
  );
};

MyGoalsSidebar.defaultProps = {
  activeGoalId: null,
  goals: [],
};

MyGoalsSidebar.propTypes = {
  activeGoalId: PropTypes.string,
  goals: PropTypes.array,
  createNew: PropTypes.bool.isRequired,
  updateGoalCollection: PropTypes.func.isRequired,
};

export default MyGoalsSidebar;
